import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-6",
  "data-testid": "handover-details-page"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "h-[120px] bg-gray-100 rounded-[12px] animate-pulse" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "h-[220px] bg-gray-100 rounded-[12px] animate-pulse" }, null, -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "h-[420px] bg-gray-100 rounded-[12px] animate-pulse" }, null, -1)
const _hoisted_5 = {
  class: "flex flex-col lg:flex-row lg:items-center gap-5 bg-white rounded-[12px] border-[1.5px] border-primary30 p-[20px]",
  "data-testid": "handover-details-summary"
}
const _hoisted_6 = { class: "flex flex-col gap-2 min-w-0" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-semibold text-[#7f7f8e] text-left" }, " Profissional que passou ", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "hidden lg:block w-px self-stretch bg-[#e8e8ed] lg:ml-auto" }, null, -1)
const _hoisted_9 = { class: "flex flex-col gap-2 lg:w-[200px]" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-semibold text-[#7f7f8e] text-left" }, "Status", -1)
const _hoisted_11 = { class: "flex" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("div", { class: "hidden lg:block w-px self-stretch bg-[#e8e8ed]" }, null, -1)
const _hoisted_13 = { class: "flex flex-col gap-2 lg:w-[260px]" }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-semibold text-[#7f7f8e] text-left" }, "Respostas", -1)
const _hoisted_15 = {
  class: "text-sm text-[#16161d] text-left",
  "data-testid": "handover-details-accepted-count"
}
const _hoisted_16 = ["aria-valuenow", "aria-valuemax"]
const _hoisted_17 = {
  key: 1,
  class: "text-sm text-[#7f7f8e] text-left"
}
const _hoisted_18 = { class: "flex flex-col bg-white rounded-[12px] border border-[#d8d8e2]" }
const _hoisted_19 = /*#__PURE__*/_createElementVNode("div", { class: "flex items-center justify-between px-[24px] py-[16px] bg-[#f8f8fa] border-b border-[#d8d8e2] rounded-t-[12px]" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "text-base font-bold text-[#16161d] text-left" }, "Convidados")
], -1)
const _hoisted_20 = { class: "px-[24px] py-[20px]" }
const _hoisted_21 = { class: "[&>div]:!rounded-[12px] [&>div>div:first-child]:!rounded-t-[12px] [&>div>div:first-child]:!rounded-b-none" }
const _hoisted_22 = {
  key: 2,
  class: "flex items-center justify-center py-[64px] text-sm text-[#7f7f8e]",
  "data-testid": "handover-details-not-found"
}

import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import DoctorInfo from '@/components/DoctorInfo.vue'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'
import CandidatesList from './components/CandidatesList.vue'
import HandoverStatusBadge from './components/HandoverStatusBadge.vue'
import PackageDetailCard from './components/PackageDetailCard.vue'
import { getAcceptedCount, getAcceptedPercent, getInviteCount } from './handoverProgress'


export default /*#__PURE__*/_defineComponent({
  __name: 'HandoverDetails',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const handoverStore = useHandoverStore()
const headerStore = useHeaderStore()

// Mesmo slot da avaliação: as duas páginas nunca ficam montadas ao mesmo tempo.
const { currentEvaluation, isLoadingEvaluation } = storeToRefs(handoverStore)

const packageData = computed(() => currentEvaluation.value?.package ?? null)

// Qualquer status diferente de PENDING sai da página (guard no onMounted).
const isRedirecting = computed(
  () => !!packageData.value?.status && packageData.value.status !== PackageStatus.PENDING
)

const inviteCount = computed(() => (packageData.value ? getInviteCount(packageData.value) : 0))
const acceptedCount = computed(() => (packageData.value ? getAcceptedCount(packageData.value) : 0))
const acceptedPercent = computed(() =>
  packageData.value ? getAcceptedPercent(packageData.value) : 0
)

// Setada no onUnmounted: a resposta da busca pode chegar depois que o usuário saiu.
let isUnmounted = false

onMounted(async () => {
  headerStore.setTitle(
    'Detalhes da troca',
    'Acompanhe quem foi convidado e quais plantões estão sendo passados',
    approvalsQueueBreadcrumbs('handoverApprovals')
  )

  const packageId = Number(route.params.packageId)

  // Id inválido (ex.: /aprovacoes/trocas/abc/detalhes): não dispara request com NaN.
  if (!Number.isInteger(packageId) || packageId <= 0) {
    handoverStore.setCurrentEvaluation(null)
    return
  }

  // Reaproveita o item que a tabela já carregou; outro pacote no store nunca é exibido.
  if (currentEvaluation.value?.package.id !== packageId) {
    await handoverStore.fetchAdminQueueItem(packageId)
  }

  // Usuário saiu da página durante a busca: não sequestra a navegação dele.
  if (isUnmounted) return

  // Fica na página quando não há o que redirecionar: erro/404 (currentEvaluation = null →
  // "Nenhuma troca encontrada") ou troca PENDING (em transição → renderiza os detalhes).
  if (!isRedirecting.value) return
  if (packageData.value?.status === PackageStatus.IN_ADMIN_QUEUE) {
    router.replace({ name: 'HandoverEvaluation', params: { packageId: String(packageId) } })
  } else {
    router.replace(approvalsQueueLocation('handoverApprovals'))
  }
})

onUnmounted(() => {
  isUnmounted = true
  handoverStore.setCurrentEvaluation(null)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isLoadingEvaluation) || isRedirecting.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _hoisted_2,
          _hoisted_3,
          _hoisted_4
        ], 64))
      : (packageData.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _hoisted_7,
                _createVNode(DoctorInfo, {
                  name: packageData.value.requester.name,
                  subtitle: `CRM ${packageData.value.requester.crm}`,
                  "avatar-url": packageData.value.requester.avatarUrl,
                  "nps-score": packageData.value.requester.npsScore,
                  size: "md"
                }, null, 8, ["name", "subtitle", "avatar-url", "nps-score"])
              ]),
              _hoisted_8,
              _createElementVNode("div", _hoisted_9, [
                _hoisted_10,
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(HandoverStatusBadge, {
                    status: packageData.value.status
                  }, null, 8, ["status"])
                ])
              ]),
              _hoisted_12,
              _createElementVNode("div", _hoisted_13, [
                _hoisted_14,
                (inviteCount.value > 0)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("span", _hoisted_15, [
                        _createElementVNode("b", null, _toDisplayString(acceptedCount.value) + " de " + _toDisplayString(inviteCount.value), 1),
                        _createTextVNode(" aceitaram ")
                      ]),
                      _createElementVNode("div", {
                        class: "h-1.5 w-full overflow-hidden rounded-full bg-[#e2e2e9]",
                        role: "progressbar",
                        "aria-label": "Convidados que aceitaram",
                        "aria-valuenow": acceptedCount.value,
                        "aria-valuemin": "0",
                        "aria-valuemax": inviteCount.value,
                        "data-testid": "handover-details-progress"
                      }, [
                        _createElementVNode("div", {
                          class: "h-full rounded-full bg-[#198038]",
                          style: _normalizeStyle({ width: `${acceptedPercent.value}%` })
                        }, null, 4)
                      ], 8, _hoisted_16)
                    ], 64))
                  : (_openBlock(), _createElementBlock("span", _hoisted_17, "—"))
              ])
            ]),
            _createElementVNode("div", _hoisted_18, [
              _hoisted_19,
              _createElementVNode("div", _hoisted_20, [
                _createVNode(CandidatesList, {
                  invites: packageData.value.invites,
                  columns: 2
                }, null, 8, ["invites"])
              ])
            ]),
            _createElementVNode("div", _hoisted_21, [
              _createVNode(PackageDetailCard, { "package-data": packageData.value }, null, 8, ["package-data"])
            ])
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_22, " Nenhuma troca encontrada "))
  ]))
}
}

})